import { OpenAPIObject } from "@stoplight/http-spec/oas3/types";
import { PathItemObject } from "openapi3-ts/src/model/OpenApi";
import { capitalize, keys, merge, pick, set } from "lodash";

interface Server {
  url: string;
  description: string;
}

// Full API gateway base URL used to fetch the OpenAPI specs. The gateway lives
// on the .com domain regardless of which domain the docs site is served from,
// so this is decoupled from the docs-domain switcher in ../tools/domain. Set it
// fully via REACT_APP_API_BASE_URL to point a build at a different gateway.
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? "https://api-staging.remotepass.com";

const servers: Server[] = [
  {
    url: "https://api-staging.remotepass.com",
    description: "Staging",
  },
  {
    url: "https://api-production.remotepass.com",
    description: "Production",
  },
];

const apis: string[] = ["users", "contract", "expense", "timeoff", "public-api"];

const publicApiTagLabels: Record<string, string> = {
  invoices: "Invoices (Beta)",
  webhooks: "Webhooks (Beta)",
};

const publicApiDisplayTag = (nativeTag: string | undefined): string =>
  (nativeTag && publicApiTagLabels[nativeTag]) ??
  (nativeTag ? `${capitalize(nativeTag)} (Beta)` : "Public API (Beta)");

// Most services expose their OpenAPI JSON at /openapi/<slug>/docs-json on the
// gateway. public-api is reached through the gateway's `v2` route prefix
// instead (everything under /v2/* is forwarded to it), so its spec is served
// at /v2/docs-json.
const docsPathOverrides: Record<string, string> = {
  "public-api": "v2/docs-json",
};

const docsJsonPath = (api: string): string =>
  docsPathOverrides[api] ?? `openapi/${api}/docs-json`;

const serviceTagLabels: Record<string, string> = {
  webhooks: "Webhooks",
};

const normalizeServiceTags = (
  tags: Array<{ name?: string }> | undefined
): Array<{ name?: string }> | undefined =>
  tags?.map((tag) =>
    tag?.name && serviceTagLabels[tag.name]
      ? { ...tag, name: serviceTagLabels[tag.name] }
      : tag
  );

const excludes = [
  "/openapi/contract/v1/status",
  "/openapi/contract/v1/attribute",
];

const transformOperation = (
  operation: PathItemObject,
  url: string
): PathItemObject => {
  set(
    operation,
    "parameters",
    operation.parameters?.map((p) =>
      "name" in p && p?.name === "x-company-id"
        ? { ...p, name: "x-api-key" }
        : p
    )
  );

  if (["Country", "Currency"].includes(operation?.tags?.[0])) {
    set(operation, "tags", ["Collections"]);
  } else if (url === "public-api") {
    set(operation, "tags", [publicApiDisplayTag(operation?.tags?.[0])]);
  } else {
    set(operation, "tags", [capitalize(url)]);
  }

  return operation;
};

const transformPaths = (
  paths: Record<string, PathItemObject>,
  api: string
): Record<string, PathItemObject> => {
  const transformedPaths: Record<string, PathItemObject> = {};
  for (const pathKey of Object.keys(paths)) {
    const pathItem = paths[pathKey];
    const transformedOperations: Record<string, PathItemObject> = {};
    for (const operationKey of Object.keys(pathItem)) {
      const operation = pathItem[operationKey];
      transformedOperations[operationKey] = transformOperation(operation, api);
    }
    transformedPaths[pathKey] = transformedOperations;
  }
  return transformedPaths;
};

const fetchApiDocs = async (
  serverUrl: string,
  api: string
): Promise<OpenAPIObject> => {
  const response = await fetch(`${serverUrl}/${docsJsonPath(api)}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch API docs for ${api}`);
  }
  const responseData = await response.json();
  const transformedPaths = transformPaths(responseData.paths, api);
  return { ...responseData, paths: transformedPaths };
};

export const getDocs = async (): Promise<OpenAPIObject[]> => {
  try {
    const requests = apis.map((api) => fetchApiDocs(API_BASE_URL, api));
    return await Promise.all(requests)
      .then((res) => merge({}, ...res))
      .then((res) => ({
        ...res,
        servers,
        tags: normalizeServiceTags(res.tags),
        paths: pick(
          res.paths,
          keys(res.paths).filter(
            (i) => !(excludes.includes(i) || i.includes("healthy"))
          )
        ),
      }));
  } catch (error) {
    console.error("Error fetching API docs:", error);
    throw error;
  }
};
